import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  StatTile,
} from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import {
  exportDoctorRevenue,
  exportMyRevenue,
  useDoctorRevenue,
  useDoctorRevenueDay,
  useMyRevenue,
  useMyRevenueDay,
  useRevenueDoctors,
  type DoctorRevenueDay,
  type DoctorRevenuePeriod,
  type RevenueCalendarDay,
  type RevenueVisit,
} from '@/features/revenue/api';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { todayKl } from '@/pages/calendar/time';
import { useSession } from '@/features/auth/session-context';
import styles from './RevenuePage.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

export type RevenuePageMode = 'own' | 'managed';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function dateFromIso(date: string) {
  return new Date(`${date}T00:00:00Z`);
}

function shiftDate(date: string, days: number) {
  const value = dateFromIso(date);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

function displayDate(date: string) {
  return new Intl.DateTimeFormat('en-MY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(dateFromIso(date));
}

function displayMonth(month: string) {
  return new Intl.DateTimeFormat('en-MY', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(dateFromIso(`${month}-01`));
}

function daysInMonth(month: string) {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
}

function firstDayOffset(month: string) {
  const weekday = dateFromIso(`${month}-01`).getUTCDay();
  return weekday === 0 ? 6 : weekday - 1;
}

function errorBody(error: unknown, t: ReturnType<typeof useTranslation>['t']) {
  if (!isApiError(error)) return undefined;
  switch (error.code) {
    case 'REVENUE_SCOPE_FORBIDDEN':
    case 'FORBIDDEN':
      return t('revenue.errors.forbidden');
    case 'REVENUE_DOCTOR_NOT_FOUND':
      return t('revenue.errors.doctorNotFound');
    case 'REVENUE_PERIOD_INVALID':
    case 'VALIDATION_FAILED':
      return t('revenue.errors.invalidPeriod');
    default:
      return undefined;
  }
}

export function RevenuePage({ mode }: { mode: RevenuePageMode }) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(todayKl().slice(0, 7));

  return (
    <PageContainer>
      {mode === 'own' ? (
        <MyRevenue month={month} onMonthChange={setMonth} />
      ) : (
        <ManagedRevenue month={month} onMonthChange={setMonth} />
      )}
      <p className={styles.basisNote}>{t('revenue.booksBasis')}</p>
    </PageContainer>
  );
}

function MyRevenue({
  month,
  onMonthChange,
}: {
  month: string;
  onMonthChange: (month: string) => void;
}) {
  const { t } = useTranslation();
  const revenue = useMyRevenue(month);

  return (
    <>
      <RevenueFilters
        month={month}
        onMonthChange={onMonthChange}
        onExport={() => exportMyRevenue(month)}
        exportDisabled={!revenue.data}
      />
      <RevenuePeriodView
        key={month}
        period={revenue.data}
        loading={revenue.isLoading}
        error={revenue.error}
        onRetry={() => void revenue.refetch()}
        loadDay={(date) => <MyRevenueDay date={date} />}
        title={t('revenue.myCalendar')}
      />
    </>
  );
}

function ManagedRevenue({
  month,
  onMonthChange,
}: {
  month: string;
  onMonthChange: (month: string) => void;
}) {
  const { t } = useTranslation();
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const doctors = useRevenueDoctors();
  const doctorId = doctors.data?.some(
    (doctor) => doctor.id === selectedDoctorId,
  )
    ? selectedDoctorId
    : (doctors.data?.[0]?.id ?? '');
  const revenue = useDoctorRevenue(doctorId, month);

  return (
    <>
      <RevenueFilters
        month={month}
        onMonthChange={onMonthChange}
        doctors={doctors.data}
        doctorId={doctorId}
        doctorsLoading={doctors.isLoading}
        doctorsError={doctors.error}
        onRetryDoctors={() => void doctors.refetch()}
        onDoctorChange={setSelectedDoctorId}
        onExport={() => exportDoctorRevenue(doctorId, month)}
        exportDisabled={!revenue.data || !doctorId}
      />
      {!doctors.isLoading && !doctors.error && !doctorId ? (
        <Card title={t('revenue.doctorRevenue')}>
          <EmptyState
            title={t('revenue.noDoctors')}
            body={t('revenue.noDoctorsBody')}
          />
        </Card>
      ) : doctorId ? (
        <RevenuePeriodView
          key={`${doctorId}:${month}`}
          period={revenue.data}
          loading={revenue.isLoading}
          error={revenue.error}
          onRetry={() => void revenue.refetch()}
          loadDay={(date) => (
            <ManagedRevenueDay doctorId={doctorId} date={date} />
          )}
          title={t('revenue.doctorCalendar')}
        />
      ) : null}
    </>
  );
}

function RevenueFilters({
  month,
  onMonthChange,
  doctors,
  doctorId,
  doctorsLoading,
  doctorsError,
  onRetryDoctors,
  onDoctorChange,
  onExport,
  exportDisabled,
}: {
  month: string;
  onMonthChange: (month: string) => void;
  doctors?: { id: string; name: string; branchName: string }[];
  doctorId?: string;
  doctorsLoading?: boolean;
  doctorsError?: unknown;
  onRetryDoctors?: () => void;
  onDoctorChange?: (doctorId: string) => void;
  onExport: () => Promise<void>;
  exportDisabled: boolean;
}) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);

  const runExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await onExport();
    } catch (error) {
      setExportError(error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card
      title={t('revenue.period')}
      actions={
        <Button
          size="sm"
          icon={<Download size={15} />}
          loading={exporting}
          disabled={exportDisabled}
          onClick={() => void runExport()}
        >
          {t('revenue.export')}
        </Button>
      }
    >
      <div className={styles.filters}>
        {onDoctorChange && (
          <Field label={t('revenue.doctor')}>
            <Select
              aria-label={t('revenue.doctor')}
              value={doctorId}
              disabled={doctorsLoading || !!doctorsError}
              placeholder={
                doctorsLoading ? t('common.loading') : t('revenue.chooseDoctor')
              }
              onChange={(event) => onDoctorChange(event.target.value)}
              options={(doctors ?? []).map((doctor) => ({
                value: doctor.id,
                label: `${doctor.name} · ${doctor.branchName}`,
              }))}
            />
          </Field>
        )}
        <Field label={t('revenue.month')}>
          <Input
            aria-label={t('revenue.month')}
            type="month"
            required
            value={month}
            onChange={(event) => onMonthChange(event.target.value)}
          />
        </Field>
      </div>
      {Boolean(doctorsError) && (
        <ErrorState error={doctorsError} onRetry={onRetryDoctors} />
      )}
      {Boolean(exportError) && (
        <ErrorState
          error={exportError}
          title={t('revenue.errors.exportFailed')}
          body={errorBody(exportError, t)}
          onRetry={() => void runExport()}
        />
      )}
    </Card>
  );
}

function RevenuePeriodView({
  period,
  loading,
  error,
  onRetry,
  loadDay,
  title,
}: {
  period: DoctorRevenuePeriod | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  loadDay: (date: string) => React.ReactNode;
  title: string;
}) {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  if (loading) return <LoadingState label={t('revenue.loading')} />;
  if (error)
    return (
      <ErrorState error={error} body={errorBody(error, t)} onRetry={onRetry} />
    );
  if (!period) return null;

  return (
    <>
      <RevenueSummary period={period} />
      <Card title={`${title} · ${displayMonth(period.month)}`}>
        <RevenueCalendar
          month={period.month}
          days={period.days}
          selectedDay={selectedDay}
          onSelect={setSelectedDay}
        />
      </Card>
      {selectedDay && (
        <Card title={t('revenue.dayDetail')}>
          <div className={styles.dayNavigation}>
            <Button
              size="sm"
              variant="ghost"
              icon={<ChevronLeft size={16} />}
              onClick={() => setSelectedDay(shiftDate(selectedDay, -1))}
            >
              {t('revenue.previousDay')}
            </Button>
            <strong>{displayDate(selectedDay)}</strong>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedDay(shiftDate(selectedDay, 1))}
            >
              {t('revenue.nextDay')}
              <ChevronRight size={16} aria-hidden="true" />
            </Button>
          </div>
          {loadDay(selectedDay)}
        </Card>
      )}
    </>
  );
}

function RevenueSummary({ period }: { period: DoctorRevenuePeriod }) {
  const { t } = useTranslation();
  const totals = period.totals;
  return (
    <div className={styles.summary} aria-label={t('revenue.periodSummary')}>
      <StatTile
        label={t('revenue.visits')}
        value={totals.visits}
        caption={period.doctor.name}
      />
      <StatTile
        label={t('revenue.gross')}
        value={formatRM(totals.grossSen)}
        caption={period.month}
      />
      <StatTile label={t('revenue.panel')} value={formatRM(totals.panelSen)} />
      <StatTile
        label={t('revenue.selfPay')}
        value={formatRM(totals.selfPaySen)}
      />
      <StatTile
        label={t('revenue.doctorShare')}
        value={formatRM(totals.doctorShareSen)}
        highlight
      />
    </div>
  );
}

function RevenueCalendar({
  month,
  days,
  selectedDay,
  onSelect,
}: {
  month: string;
  days: RevenueCalendarDay[];
  selectedDay: string | null;
  onSelect: (date: string) => void;
}) {
  const { t } = useTranslation();
  const byDate = new Map(days.map((day) => [day.date, day]));
  const cells = Array.from({ length: daysInMonth(month) }, (_, index) => {
    const dayNumber = index + 1;
    const date = `${month}-${String(dayNumber).padStart(2, '0')}`;
    return { date, dayNumber, revenue: byDate.get(date) };
  });

  return (
    <div className={styles.calendar}>
      <div className={styles.weekdays} aria-hidden="true">
        {WEEKDAYS.map((weekday) => (
          <span key={weekday}>{weekday}</span>
        ))}
      </div>
      <div className={styles.calendarGrid}>
        {Array.from({ length: firstDayOffset(month) }, (_, index) => (
          <span key={`blank-${index}`} className={styles.blankDay} />
        ))}
        {cells.map(({ date, dayNumber, revenue }) => (
          <button
            key={date}
            type="button"
            className={[
              styles.calendarDay,
              selectedDay === date && styles.selectedDay,
              revenue?.visits && styles.revenueDay,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={selectedDay === date}
            aria-label={t('revenue.calendarDayLabel', {
              date: displayDate(date),
              visits: revenue?.visits ?? 0,
            })}
            onClick={() => onSelect(date)}
          >
            <span className={styles.dayNumber}>{dayNumber}</span>
            {revenue ? (
              <>
                <span className={styles.dayAmount}>
                  {formatRM(revenue.grossSen)}
                </span>
                <span className={styles.dayVisits}>
                  {t('revenue.visitCount', { count: revenue.visits })}
                </span>
              </>
            ) : (
              <span className={styles.dayVisits}>{t('revenue.noFigures')}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function MyRevenueDay({ date }: { date: string }) {
  const day = useMyRevenueDay(date);
  return (
    <RevenueDayView
      day={day.data}
      loading={day.isLoading}
      error={day.error}
      onRetry={() => void day.refetch()}
    />
  );
}

function ManagedRevenueDay({
  doctorId,
  date,
}: {
  doctorId: string;
  date: string;
}) {
  const day = useDoctorRevenueDay(doctorId, date);
  return (
    <RevenueDayView
      day={day.data}
      loading={day.isLoading}
      error={day.error}
      onRetry={() => void day.refetch()}
    />
  );
}

function RevenueDayView({
  day,
  loading,
  error,
  onRetry,
}: {
  day: DoctorRevenueDay | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  // Review: only people who can open Books are pointed there.
  const canBooks = useSession().hasPermission('books:read');
  const notRosteredError =
    isApiError(error) && error.code === 'REVENUE_DAY_NOT_ON_ROSTER';

  if (loading) return <LoadingState label={t('revenue.loadingDay')} />;
  if (notRosteredError || (day && !day.onRoster))
    return (
      <EmptyState
        icon={<CalendarDays size={22} />}
        title={t('revenue.notOnRoster')}
        body={t('revenue.notOnRosterBody')}
      />
    );
  if (error)
    return (
      <ErrorState error={error} body={errorBody(error, t)} onRetry={onRetry} />
    );
  if (!day) return null;

  return (
    <div className={styles.dayDetail}>
      <div className={styles.dayTotals}>
        <span>
          {t('revenue.visits')} <strong>{day.totals.visits}</strong>
        </span>
        <span>
          {t('revenue.gross')} <strong>{formatRM(day.totals.grossSen)}</strong>
        </span>
        <span>
          {t('revenue.doctorShare')}{' '}
          <strong>{formatRM(day.totals.doctorShareSen)}</strong>
        </span>
      </div>
      {day.totals.grossSen > 0 && day.totals.doctorShareSen === 0 && (
        <p className={styles.payBasisNote} role="note">
          {t(canBooks ? 'revenue.noPayBasis' : 'revenue.noPayBasisAsk')}
        </p>
      )}
      <DataTable
        caption={t('revenue.visitsForDay', { date: displayDate(day.date) })}
        rows={day.visits}
        rowKey={(visit) => visit.id}
        empty={
          <EmptyState
            title={t('revenue.noClosedVisits')}
            body={t('revenue.noClosedVisitsBody')}
          />
        }
        columns={visitColumns(t, canBooks)}
      />
    </div>
  );
}

function visitColumns(
  t: ReturnType<typeof useTranslation>['t'],
  canBooks: boolean,
) {
  return [
    {
      key: 'patient',
      header: t('revenue.patient'),
      render: (visit: RevenueVisit) => <strong>{visit.patientDisplay}</strong>,
    },
    {
      key: 'visit',
      header: t('revenue.visit'),
      render: (visit: RevenueVisit) => visit.visitNumber,
    },
    {
      key: 'closed',
      header: t('revenue.closedAt'),
      render: (visit: RevenueVisit) =>
        new Intl.DateTimeFormat('en-MY', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: CLINIC_TZ,
        }).format(new Date(visit.closedAt)),
    },
    {
      key: 'payer',
      header: t('revenue.payer'),
      render: (visit: RevenueVisit) => (
        <div className={styles.payer}>
          <Badge tone={visit.payerType === 'panel' ? 'info' : 'neutral'}>
            {t(`revenue.payerType.${visit.payerType}`)}
          </Badge>
          {visit.payerName && <span>{visit.payerName}</span>}
        </div>
      ),
    },
    {
      key: 'gross',
      header: t('revenue.gross'),
      align: 'right' as const,
      render: (visit: RevenueVisit) => formatRM(visit.grossSen),
    },
    {
      key: 'share',
      header: t('revenue.doctorShare'),
      align: 'right' as const,
      render: (visit: RevenueVisit) => (
        <>
          <strong>{formatRM(visit.doctorShareSen)}</strong>
          {canBooks && visit.doctorShareSen === 0 && visit.grossSen > 0 && (
            <span className={styles.payBasisHint}>
              {t('revenue.setPayBasis')}
            </span>
          )}
        </>
      ),
    },
  ];
}
